import { useTranslation } from "react-i18next";
import { CodeDescriptionDto, PreprocessingFileDto } from "api/api.typing";
import BatchMultiSelect from "components/Dropdown/BatchMultiSelect";
import SearchInput from "components/input/SearchInput";
import { NO_LANGUAGE_CODE, NO_SOURCE_FILE_ID } from "./fileMappingHelpers";

interface FileMappingToolbarProps {
  files: File[];
  sourceFiles: PreprocessingFileDto[];
  targetLanguages: CodeDescriptionDto[];
  selectedFiles: Set<string>;
  batchSourceFiles: number[];
  batchLanguages: string[];
  onSelectAll: (checked: boolean) => void;
  onBatchSourceFilesChange: (values: number[]) => void;
  onBatchLanguagesChange: (values: string[]) => void;
  onApplyToSelected: () => void;
  searchTerm: string;
  onSearchChange: (value: string | null) => void;
}

export function FileMappingToolbar({
  files,
  sourceFiles,
  targetLanguages,
  selectedFiles,
  batchSourceFiles,
  batchLanguages,
  onSelectAll,
  onBatchSourceFilesChange,
  onBatchLanguagesChange,
  onApplyToSelected,
  searchTerm,
  onSearchChange,
}: FileMappingToolbarProps) {
  const { t: translation } = useTranslation();

  return (
    <div className="mt-4 rounded-md border border-gray-300 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedFiles.size === files.length && files.length > 0}
            onChange={(e) => onSelectAll(e.target.checked)}
            className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium">{translation("task.upload.file-mapping.select-all")}</span>
        </label>

        <div className="ml-6 flex items-center gap-3">
          <SearchInput
            term={searchTerm}
            onSearch={onSearchChange}
            placeholder={translation("task.upload.file-mapping.search-files")}
            className="w-60 flex-none"
          />
        </div>

        <BatchMultiSelect<number>
          value={batchSourceFiles}
          onChange={onBatchSourceFilesChange}
          items={sourceFiles.map((f) => ({ id: f.id, name: f.fileName }))}
          placeholder={translation("task.upload.file-mapping.source-batch")}
          withNone={true}
          noneValue={NO_SOURCE_FILE_ID}
          className="min-w-[265px]"
        />

        <BatchMultiSelect<string>
          value={batchLanguages}
          onChange={onBatchLanguagesChange}
          items={targetLanguages.map((l) => ({ id: l.code, name: l.description }))}
          placeholder={translation("task.upload.file-mapping.languages-batch")}
          withNone={true}
          noneValue={NO_LANGUAGE_CODE}
          className="min-w-[250px]"
        />

        <button
          type="button"
          disabled={selectedFiles.size === 0}
          onClick={onApplyToSelected}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {translation("task.upload.file-mapping.apply-to-selected")}
        </button>
      </div>
    </div>
  );
}
